import {DatePipe} from "@angular/common";
import {environment} from "src/environments/environment";
import {HttpErrorResponse} from "@angular/common/http";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from "@angular/core";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {
  EMPTY,
  Subject,
  asapScheduler,
  audit,
  catchError,
  filter,
  finalize,
  expand,
  reduce,
  merge,
  of,
  startWith,
  switchMap,
  timer,
} from "rxjs";
import {AuthService} from "src/app/modules/auth/services/auth.service";
import {
  DiscussionDraft,
  DiscussionEntry,
} from "../../interfaces/discussion.interface";
import {
  RecommendationThreadPage,
  RecommendationThreadSummary,
} from "../../interfaces/recommendation-thread.interface";
import {RecommendationDiscussionsService} from "../../services/recommendation-discussions.service";
import {DiscussionThreadComponent} from "../discussion-thread/discussion-thread.component";
import {DiscussionTextComponent} from "../discussion-text/discussion-text.component";
import {UserAvatarComponent} from "src/app/modules/shared/components/user-avatar/user-avatar.component";
import {ScreenSizeService} from "src/app/modules/shared/services/screenSize.service";
import {WorksService} from "src/app/modules/shared/services/works.service";
import {ModalService} from "src/app/modules/core/services/modal.service";
import {ToastService} from "src/app/modules/core/services/toast.service";
import {UserSafetyService} from "src/app/modules/shared/services/user-safety.service";
import {ReportReason} from "../../enums/report-reason.enum";
import {DiscussionReactionsState} from "../../services/discussion-reactions-state.service";
import {DiscussionTypingState} from "../../services/discussion-typing-state.service";
import {DiscussionPresenceState} from "../../services/discussion-presence-state.service";
import {DiscussionVisibilityService} from "../../services/discussion-visibility.service";
import {DiscussionReactionsComponent} from "../discussion-reactions/discussion-reactions.component";
import {DiscussionGestureDirective} from "../../directives/discussion-gesture.directive";
import {DiscussionCopyComponent} from "../discussion-copy/discussion-copy.component";
import {DiscussionPanelHeaderComponent} from "../discussion-panel-header/discussion-panel-header.component";
import {DiscussionLoadingComponent} from "../discussion-loading/discussion-loading.component";
import {convertToParamMap} from "@angular/router";
import type {PublicWorkResponse} from "src/app/modules/works/interfaces/public-work.interface";
import {PublicWorkEntryService} from "src/app/modules/works/services/public-work-entry.service";

@Component({
  selector: "app-recommendation-discussion",
  templateUrl: "./recommendation-discussion.component.html",
  imports: [
    DiscussionPanelHeaderComponent,
    DiscussionLoadingComponent,
    DatePipe,
    TranslateModule,
    DiscussionThreadComponent,
    DiscussionTextComponent,
    UserAvatarComponent,
    DiscussionReactionsComponent,
    DiscussionGestureDirective,
    DiscussionCopyComponent,
  ],
  providers: [
    DiscussionReactionsState,
    DiscussionTypingState,
    DiscussionPresenceState,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RecommendationDiscussionComponent implements OnInit {
  readonly imagesDomain = environment.imagesDomain;
  coverFailed = false;
  @Input({required: true}) threadId!: string;
  scrolled = false;
  @Output() reportRequested = new EventEmitter<void>();
  @Output() blockRequested = new EventEmitter<void>();
  @Output() safetyCompleted = new EventEmitter<void>();
  private readonly destroyRef = inject(DestroyRef);
  private readonly publicEntry = inject(PublicWorkEntryService);
  private readonly refresh = new Subject<void>();
  readonly reactions = inject(DiscussionReactionsState);
  readonly typing = inject(DiscussionTypingState);
  private readonly presence = inject(DiscussionPresenceState);
  private readonly visibility = inject(DiscussionVisibilityService);
  private paginated = false;
  private pageGeneration = 0;
  thread?: RecommendationThreadSummary;
  entries: DiscussionEntry[] = [];
  before?: number;
  unreadAfter?: number;
  userId = "";
  locale = "en";
  recipient = "";
  suggestions: string[] = [];
  error = "";
  loading = true;
  loadingMore = false;
  openingWork = false;
  sending = false;
  deleting = false;
  reportMessageId?: string;
  reporting = false;
  blocking = false;
  private readonly safety = inject(UserSafetyService);
  sentRevision = 0;
  private markedThrough = -1;
  private latestSequence = 0;
  peerReadSequence = 0;

  constructor(
    public readonly api: RecommendationDiscussionsService,
    private readonly auth: AuthService,
    private readonly cdr: ChangeDetectorRef,
    private readonly translate: TranslateService,
    private readonly works: WorksService,
    private readonly modal: ModalService,
    private readonly toast: ToastService,
    public readonly screenSize: ScreenSizeService
  ) {}

  ngOnInit() {
    this.userId = this.auth.user?._id ?? "";
    this.api.updates$
      .pipe(
        filter(
          (event) =>
            event.threadId === this.threadId &&
            event.kind === "read" &&
            event.readerId !== this.userId
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event) => {
        this.peerReadSequence = Math.max(
          this.peerReadSequence,
          event.readSequence ?? 0
        );
        this.cdr.markForCheck();
      });
    this.reactions.initialize(this.threadId, this.userId);
    this.typing.initialize(this.threadId);
    this.reactions.rightsChanged$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refresh.next());
    this.reactions.changed$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((state) => {
        if (state.status !== "available") {
          const palette = document.activeElement?.closest(
            "[data-reaction-palette]"
          );
          const targetKey =
            state.target === "comment" ? "comment" : state.messageId;
          if (palette?.getAttribute("data-reaction-target") === targetKey) {
            document
              .getElementById("recommendation-thread-title")
              ?.focus({preventScroll: true});
          }
          if (state.target === "comment" && this.thread) {
            this.thread = {
              ...this.thread,
              comment: undefined,
              commentModeratedAt: state.moderatedAt,
              commentReactions: [],
              commentReactionsRevision: state.revision,
            };
          } else {
            this.entries = this.entries.map((entry) =>
              entry._id === state.messageId
                ? {
                    ...entry,
                    text: undefined,
                    reactions: [],
                    reactionsRevision: state.revision,
                    spoiler: false,
                    deletedAt:
                      state.deletedAt ??
                      entry.deletedAt ??
                      (state.status === "unavailable"
                        ? entry.createdAt
                        : undefined),
                    moderatedAt: state.moderatedAt ?? entry.moderatedAt,
                  }
                : entry
            );
          }
        }
        this.cdr.markForCheck();
      });
    this.locale =
      this.auth.user?.language || this.translate.currentLang || "en";
    merge(
      this.refresh,
      this.api.resumed$,
      this.api.socket.ready$.pipe(filter(Boolean)),
      this.api.updates$.pipe(
        filter(
          (event) => event.threadId === this.threadId && event.kind !== "read"
        )
      )
    )
      .pipe(
        startWith(null),
        // Hydrate the prefetched page before paint while coalescing startup events.
        audit(() => timer(this.loading ? 0 : 120, asapScheduler)),
        switchMap(() => {
          this.pageGeneration++;
          return this.latestPage().pipe(
            catchError((error: unknown) => {
              if (
                error instanceof HttpErrorResponse &&
                (error.status === 403 || error.status === 404)
              ) {
                this.typing.configure("", false);
                if (this.thread)
                  this.reactions.hydrate({...this.thread, canReply: false}, []);
                this.api.close();
                this.toast.sendToast({
                  text: "DISCUSSION.LOAD_ERROR",
                  classes: "bg-form-error",
                });
              }
              this.loading = false;
              this.error = "DISCUSSION.LOAD_ERROR";
              this.cdr.markForCheck();
              return EMPTY;
            })
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((page) => {
        if (!this.thread) {
          this.unreadAfter = page.thread.unreadCount
            ? page.thread.readSequence
            : undefined;
        }
        this.thread = {
          ...page.thread,
          commentModeratedAt:
            this.thread?.commentModeratedAt ?? page.thread.commentModeratedAt,
        };
        this.api.rememberContext(page.thread);
        this.latestSequence = Math.max(
          this.latestSequence,
          page.latestSequence
        );
        this.peerReadSequence = Math.max(
          this.peerReadSequence,
          page.peerReadSequence
        );
        this.mergePage(page);
        this.typing.configure(
          page.thread.sender._id === this.userId
            ? page.thread.receiver._id
            : page.thread.sender._id,
          page.thread.canReply
        );
        this.recipient = (
          page.thread.sender._id === this.userId
            ? page.thread.receiver
            : page.thread.sender
        ).username;
        const role =
          page.thread.sender._id === this.userId ? "SENDER" : "RECEIVER";
        const context = page.thread.receiverCompleted ? "AFTER" : "BEFORE";
        this.suggestions = this.translate.instant(
          `DISCUSSION.QUICK_${role}_${context}`
        );
        this.loading = false;
        this.error = "";
        this.presence.start(this.threadId, () => this.refresh.next());
        this.markRead();
        this.cdr.markForCheck();
      });
  }

  private mergePage(page: RecommendationThreadPage): void {
    const incoming = [...page.entries, ...page.history];
    const incomingIds = new Set(incoming.map((entry) => entry._id));
    const olderTargets = this.entries
      .filter(
        (entry) => entry.kind === "message" && !incomingIds.has(entry._id)
      )
      .map((entry) => entry._id);
    const previousLast = Math.max(
      0,
      ...this.entries.map((entry) => entry.sequence)
    );
    // A long absence can leave a gap between retained pages and the new latest page.
    if (!this.paginated || (page.before ?? 0) > previousLast)
      this.before = page.before;
    const retained = this.entries.filter(
      (entry) =>
        entry.kind === "message" || (page.canSeeActivity && entry.sequence > 0)
    );
    const existing = new Map(retained.map((entry) => [entry._id, entry]));
    for (const entry of incoming) {
      const previous = existing.get(entry._id);
      existing.set(
        entry._id,
        previous?.deletedAt || previous?.moderatedAt ? previous : entry
      );
    }
    this.entries = [...existing.values()];
    this.reactions.hydrate(page.thread, incoming);
    this.reactions.invalidate(olderTargets);
  }

  private latestPage() {
    const previousLast = Math.max(
      0,
      ...this.entries.map((entry) => entry.sequence)
    );
    return this.api.read(this.threadId).pipe(
      expand((page) =>
        previousLast && page.before && page.before > previousLast
          ? this.api.read(this.threadId, page.before)
          : EMPTY
      ),
      reduce(
        (
          all: RecommendationThreadPage | undefined,
          page
        ): RecommendationThreadPage =>
          all
            ? {
                ...all,
                before: page.before,
                entries: [...page.entries, ...all.entries],
              }
            : page,
        undefined
      ),
      filter((page): page is RecommendationThreadPage => !!page)
    );
  }

  retry() {
    if (!this.thread) this.loading = true;
    this.error = "";
    this.refresh.next();
  }

  showReport(messageId?: string): void {
    if (this.reporting || this.blocking) return;
    this.reportMessageId = messageId;
    this.reportRequested.emit();
  }

  report(reason: ReportReason): void {
    if (this.reporting) return;
    this.reporting = true;
    this.api
      .report(this.threadId, reason, this.reportMessageId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.reporting = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: () => {
          this.safetyCompleted.emit();
          this.toast.sendToast({
            text: "DISCUSSION.REPORT_SENT",
            classes: "bg-form-success",
          });
        },
        error: () =>
          this.toast.sendToast({
            text: "DISCUSSION.REPORT_ERROR",
            classes: "bg-form-error",
          }),
      });
  }

  setBlocked(blocked: boolean): void {
    if (!this.thread || this.blocking) return;
    this.blocking = true;
    this.typing.stop();
    const otherId =
      this.thread.sender._id === this.userId
        ? this.thread.receiver._id
        : this.thread.sender._id;
    this.safety
      .setBlocked(otherId, blocked)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.blocking = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: () => {
          this.safetyCompleted.emit();
          this.refresh.next();
          this.toast.sendToast({
            text: blocked
              ? "DISCUSSION.BLOCKED_SUCCESS"
              : "DISCUSSION.UNBLOCKED_SUCCESS",
            classes: "bg-form-success",
          });
        },
        error: () =>
          this.toast.sendToast({
            text: "DISCUSSION.BLOCK_ERROR",
            classes: "bg-form-error",
          }),
      });
  }

  readOnlyReason(): string {
    if (this.thread?.messagingSuspended) return "SOCIAL_SUSPENDED";
    return this.thread?.blockedByMe
      ? "DISCUSSION.BLOCKED_HINT"
      : "DISCUSSION.READ_ONLY";
  }

  openWork(page?: PublicWorkResponse): void {
    const thread = this.thread;
    if (!thread || this.openingWork) return;
    const threadId = this.threadId;
    const userId = this.userId;
    this.openingWork = true;
    this.works
      .getWorkDetails(
        userId,
        page?.work.id ?? thread.work._id,
        page?.work.type ?? thread.work.type
      )
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.openingWork = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe((work) => {
        if (
          this.api.selectedId() !== threadId ||
          this.threadId !== threadId ||
          this.userId !== userId ||
          !this.api.opened() ||
          this.api.closing()
        )
          return;
        if (!work) {
          this.toast.sendToast({
            text: "DISCUSSION.WORK_UNAVAILABLE",
            classes: "bg-form-error",
          });
          return;
        }
        if (page)
          this.publicEntry.set(page, convertToParamMap({}), this.userId);
        if (this.screenSize.currentScreenSize.isMobile)
          this.api.closeForWork(work._id);
        this.modal.openWorkModal(
          work,
          this.works.getWorksDetailsComponentByType(work.type),
          true,
          undefined,
          undefined,
          undefined,
          true
        );
      });
  }

  send(draft: DiscussionDraft) {
    if (this.sending) return;
    this.sending = true;
    this.typing.stop();
    this.error = "";
    this.api
      .send(this.threadId, draft)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.sending = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: () => {
          this.sentRevision++;
          this.refresh.next();
        },
        error: () => {
          this.toast.sendToast({
            text: "DISCUSSION.SEND_ERROR",
            classes: "bg-form-error",
          });
          this.refresh.next();
        },
      });
  }

  remove(id: string) {
    if (this.deleting) return;
    this.deleting = true;
    this.reactions.cancel(id);
    this.api
      .remove(this.threadId, id)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.deleting = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: () => {
          this.reactions.invalidate([id]);
          this.refresh.next();
        },
        error: () => {
          this.error = "DISCUSSION.DELETE_ERROR";
        },
      });
  }

  older() {
    if (!this.before || this.loadingMore) return;
    this.loadingMore = true;
    this.error = "";
    const generation = this.pageGeneration;
    this.api
      .read(this.threadId, this.before)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loadingMore = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (page) => {
          if (generation !== this.pageGeneration) return;
          this.paginated = true;
          if (!page.canSeeActivity)
            this.entries = this.entries.filter(
              (entry) => entry.kind === "message"
            );
          this.entries = [
            ...[...page.entries, ...page.history].filter(
              (entry) =>
                !this.entries.some((current) => current._id === entry._id)
            ),
            ...this.entries,
          ];
          this.before = page.before;
          if (this.thread) this.reactions.hydrate(this.thread, page.entries);
        },
        error: () => {
          this.error = "DISCUSSION.LOAD_ERROR";
        },
      });
  }

  private markRead() {
    if (!this.visibility.active) return;
    const sequence = Math.max(
      this.latestSequence,
      ...this.entries.map((entry) => entry.sequence)
    );
    if (sequence <= this.markedThrough) return;
    this.markedThrough = sequence;
    this.api
      .markRead(this.threadId, sequence)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: () => {
          this.markedThrough = -1;
        },
      });
  }
}
